import { AlertCircle, Home, RefreshCw } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { getErrorActionText, StandardError } from "@/lib/error/error-utils";
import { cn } from "@/lib/utils";

interface QueryErrorDisplayProps {
  error: StandardError;
  onRetry?: () => void;
  onHome?: () => void;
  className?: string;
  size?: "sm" | "md" | "lg";
  showIcon?: boolean;
}

export function QueryErrorDisplay({
  error,
  onRetry,
  onHome,
  className,
  size = "md",
  showIcon = true,
}: QueryErrorDisplayProps) {
  const iconSizes = {
    sm: "h-8 w-8",
    md: "h-12 w-12",
    lg: "h-16 w-16",
  };

  const containerPadding = {
    sm: "py-4",
    md: "py-8",
    lg: "py-12",
  };

  return (
    <div className={cn("text-center", containerPadding[size], className)}>
      {showIcon && <AlertCircle className={cn(iconSizes[size], "text-destructive mx-auto mb-4")} />}

      <h3 className={cn("font-semibold mb-2", size === "sm" ? "text-base" : "text-lg")}>
        Unable to load data
      </h3>

      <p className={cn("text-muted-foreground mb-4", size === "sm" ? "text-sm" : "text-base")}>
        {error.message}
      </p>

      <div className="flex gap-2 justify-center">
        {onRetry && error.retryable && (
          <Button onClick={onRetry} variant="outline" size={size === "sm" ? "sm" : "default"}>
            <RefreshCw className="h-4 w-4 mr-2" />
            {getErrorActionText(error)}
          </Button>
        )}

        {onHome && (
          <Button onClick={onHome} variant="ghost" size={size === "sm" ? "sm" : "default"}>
            <Home className="h-4 w-4 mr-2" />
            Go Home
          </Button>
        )}
      </div>
    </div>
  );
}

interface InlineErrorDisplayProps {
  error: StandardError;
  onRetry?: () => void;
  className?: string;
}

export function InlineErrorDisplay({ error, onRetry, className }: InlineErrorDisplayProps) {
  return (
    <Alert variant="destructive" className={cn("", className)}>
      <AlertCircle className="h-4 w-4" />
      <div className="flex items-center justify-between">
        <AlertDescription className="flex-1">{error.message}</AlertDescription>
        {onRetry && error.retryable && (
          <Button onClick={onRetry} variant="outline" size="sm" className="ml-2">
            <RefreshCw className="h-3 w-3 mr-1" />
            Retry
          </Button>
        )}
      </div>
    </Alert>
  );
}
